import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PrescriptionLineView, PrescriptionView } from '@wodi/shared';
import { Button, Dialog, DialogActions, LoadingBlock, MenuButton, Notice, Tag } from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  changeLine,
  prescribingKeys,
  visitPrescriptionsQuery,
  type LineAction,
} from '../api/prescribing.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { quantityWords } from './draft.ts';
import { directions, PrescriptionPrinter } from './prescription-print.tsx';
import styles from './prescribing.module.css';

// The visit's signed prescriptions (plan §7.3): each line with its state in words, and
// "Stop", "Cancel" and "Entered in error" with a reason. Signed content is never edited;
// a changed dose is a new line. The pharmacy may refuse a stop (already paid); its words
// are shown as they come.

const LINE_STATUS: Record<
  PrescriptionLineView['status'],
  { word: string; state: 'active' | 'neutral' }
> = {
  active: { word: 'Signed', state: 'active' },
  stopped: { word: 'Stopped', state: 'neutral' },
  cancelled: { word: 'Cancelled', state: 'neutral' },
  entered_in_error: { word: 'Entered in error', state: 'neutral' },
};

const ACTION_WORDS: Record<
  LineAction,
  { title: string; confirm: string; pending: string; toast: string }
> = {
  stop: {
    title: 'Stop this medicine',
    confirm: 'Stop medicine',
    pending: 'Stopping…',
    toast: 'Medicine stopped',
  },
  cancel: {
    title: 'Cancel this line',
    confirm: 'Cancel line',
    pending: 'Cancelling…',
    toast: 'Line cancelled',
  },
  'entered-in-error': {
    title: 'Mark as entered in error',
    confirm: 'Mark entered in error',
    pending: 'Marking…',
    toast: 'Line marked entered in error',
  },
};

function actionsFor(line: PrescriptionLineView): LineAction[] {
  if (line.status === 'active') return ['stop', 'cancel', 'entered-in-error'];
  if (line.status === 'stopped' || line.status === 'cancelled') return ['entered-in-error'];
  return [];
}

function Line({
  line,
  mayWrite,
  onAction,
}: {
  line: PrescriptionLineView;
  mayWrite: boolean;
  onAction: (a: LineAction) => void;
}) {
  const status = LINE_STATUS[line.status];
  const available = mayWrite ? actionsFor(line) : [];
  return (
    <div className={styles.rxLine} data-inactive={line.status === 'active' ? undefined : true}>
      <Tag state={status.state}>{status.word}</Tag>
      <span className={styles.sig}>
        <strong>{line.drugDisplay}</strong>
        {`: ${directions(line)}. ${quantityWords(line.quantity, line.quantityUnit)}.`}
        {line.serviceItemId === null ? ' Buy outside.' : ''}
        {line.statusReason === null ? '' : ` Reason: ${line.statusReason}`}
      </span>
      {available.length === 0 ? null : (
        <MenuButton
          label="Change"
          items={available.map((a) => ({ id: a, label: ACTION_WORDS[a].confirm }))}
          onAction={(id) => {
            onAction(id as LineAction);
          }}
        />
      )}
    </div>
  );
}

export function VisitPrescriptions({
  visitId,
  patientId,
  mayWrite,
}: {
  visitId: string;
  patientId: string;
  mayWrite: boolean;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const list = useQuery(visitPrescriptionsQuery(visitId));
  const [pending, setPending] = useState<{ line: PrescriptionLineView; action: LineAction } | null>(
    null,
  );
  const [printing, setPrinting] = useState<PrescriptionView | null>(null);
  const change = useMutation({
    mutationFn: (reason: string) => {
      if (pending === null) throw new Error('no line chosen');
      return changeLine(pending.line.id, pending.action, reason, pending.line.version);
    },
    onSuccess: () => {
      if (pending !== null) toasts.show({ title: ACTION_WORDS[pending.action].toast });
      setPending(null);
      void queryClient.invalidateQueries({ queryKey: prescribingKeys.visit(visitId) });
      void queryClient.invalidateQueries({ queryKey: prescribingKeys.medicines(patientId) });
    },
  });

  if (list.isPending) return <LoadingBlock label="Loading prescriptions" />;
  if (list.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void list.refetch()}>Try again</Button>}>
        {list.error instanceof ApiError
          ? list.error.message
          : 'Prescriptions could not load. Try again.'}
      </Notice>
    );
  }
  if (list.data.items.length === 0) {
    return <p className={styles.empty}>No prescriptions signed in this visit yet.</p>;
  }
  return (
    <div className={styles.list}>
      {list.data.items.map((rx) => (
        <article
          key={rx.id}
          className={styles.rx}
          aria-label={`Prescription ${rx.prescriptionNumber}`}
        >
          <header className={styles.rxHeader}>
            <span className="wodi-id">{rx.prescriptionNumber}</span>
            <span>{`${formatInstant(rx.signedAt)}, ${rx.prescriberDisplay}`}</span>
            <Button
              variant="quiet"
              isDisabled={!online}
              onPress={() => {
                setPrinting(rx);
              }}
            >
              {rx.printCount > 0 ? 'Print again' : 'Print prescription'}
            </Button>
          </header>
          {rx.lines.map((line) => (
            <Line
              key={line.id}
              line={line}
              mayWrite={mayWrite && online}
              onAction={(action) => {
                change.reset();
                setPending({ line, action });
              }}
            />
          ))}
        </article>
      ))}
      <ReasonDialog
        title={pending === null ? '' : ACTION_WORDS[pending.action].title}
        isOpen={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        confirmLabel={pending === null ? '' : ACTION_WORDS[pending.action].confirm}
        pendingLabel={pending === null ? '' : ACTION_WORDS[pending.action].pending}
        isPending={change.isPending}
        error={
          change.error === null
            ? null
            : change.error instanceof ApiError
              ? change.error.message
              : 'That did not work. Try again.'
        }
        onConfirm={(reason) => {
          change.mutate(reason);
        }}
      >
        {pending === null ? null : <p>{pending.line.drugDisplay}</p>}
      </ReasonDialog>
      {printing === null ? null : (
        <Dialog
          title="Prescription"
          isOpen
          onOpenChange={(open) => {
            if (!open) setPrinting(null);
          }}
        >
          <PrescriptionPrinter prescriptionId={printing.id} visitId={visitId} />
          <DialogActions>
            <Button
              onPress={() => {
                setPrinting(null);
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}
